/**
 * DigitalInvoiceQuickModal - Invoice creation modal for Digital Invoice
 *
 * Usage:
 * 1. Standalone - from ClinicSettingsPage management tab
 * 2. Pre-filled - from PaymentKanban / PendingActionsGrid / PendingAppointmentsCarousel
 * 3. Transaction mode - from PaymentKanban with a financial transaction
 *
 * Error Codes: CK-9212, CK-9213
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import { useSelector } from "react-redux";
import { httpsCallable } from "firebase/functions";
import { functions, getAppointments } from "../../firebase";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistId,
} from "../../store/slices/authSlice";
import { useGetPatientsQuery } from "../../store/services/patientsApi";
import {
  Receipt,
  CheckCircle,
  WarningCircle,
  User,
  CurrencyCircleDollar,
  FileText,
  Eye,
  CalendarBlank,
  CaretDown,
  Check,
  LinkSimple,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import {
  formatCurrency,
  DI_DOCUMENT_TYPES,
  DI_DOCUMENT_LABELS,
  getDocumentTypeOptions,
  getPaymentTypeOptions,
  mapPaymentMethodToDI,
} from "../../services/digitalInvoiceService";
import ModalCloseButton from "../ui/ModalCloseButton";
import { sortPatientsByName } from "../../utils/patientSortUtils";
import "./DigitalInvoiceQuickModal.css";
import ContentLoader from "../shared/ContentLoader";
import LoaderLoop from "../shared/loader/LoaderLoop";

const APPOINTMENTS_PAGE_SIZE = 10;

function DigitalInvoiceQuickModal({
  isOpen,
  onClose,
  appointment: prefilledAppointment = null,
  transaction: prefilledTransaction = null,
  patientName: prefilledPatientName = "",
  amount: prefilledAmount = "",
  paymentMethod: prefilledPaymentMethod = null,
  onSuccess,
}) {
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const therapistId = useSelector(selectTherapistId);

  const diIntegration = clinicProfile?.digitalInvoiceIntegration;
  const defaultDocType = diIntegration?.defaultDocumentType || DI_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT;
  const defaultPayType = diIntegration?.defaultPaymentType || 1;

  const [isCreating, setIsCreating] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [documentType, setDocumentType] = useState(
    defaultDocType
  );
  const [paymentType, setPaymentType] = useState(
    prefilledPaymentMethod
      ? mapPaymentMethodToDI(prefilledPaymentMethod)
      : defaultPayType
  );
  const [description, setDescription] = useState("טיפול");
  const [amount, setAmount] = useState(prefilledAmount || "");
  const [patientName, setPatientName] = useState(prefilledPatientName || "");

  // Email toggle
  const [sendEmail, setSendEmail] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");

  // Patient selection
  const [selectedPatientId, setSelectedPatientId] = useState(
    prefilledAppointment?.patientId || prefilledTransaction?.patientId || null
  );
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState("");

  // Appointment selection
  const [selectedAppointments, setSelectedAppointments] = useState(
    prefilledAppointment ? [prefilledAppointment] : []
  );
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const [hasMoreAppointments, setHasMoreAppointments] = useState(false);

  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, therapistId, pageSize: 200 },
    { skip: !clinicId || !therapistId || !isOpen }
  );

  const patients = patientsData?.patients || [];

  const filteredPatients = sortPatientsByName(patients).filter((p) =>
    `${p.firstName} ${p.lastName}`
      .toLowerCase()
      .includes(patientSearchQuery.toLowerCase())
  );

  const isPrefilledMode = !!prefilledAppointment || !!prefilledTransaction;

  // Pre-fill patient email when patient is selected
  useEffect(() => {
    if (selectedPatientId && patients.length > 0) {
      const patient = patients.find((p) => p.id === selectedPatientId);
      if (patient?.email) {
        setEmailAddress(patient.email);
      }
    }
  }, [selectedPatientId, patients]);

  // Load patient appointments
  const loadPatientAppointments = useCallback(
    async (patientId, page = 1) => {
      if (!clinicId || !patientId) return;

      setAppointmentsLoading(true);
      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 6);

        const result = await getAppointments(
          clinicId,
          startDate,
          endDate,
          therapistId
        );

        const patientApts = (result || [])
          .filter(
            (apt) =>
              apt.patientId === patientId &&
              apt.status === "completed"
          )
          .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

        const pageEnd = page * APPOINTMENTS_PAGE_SIZE;
        setPatientAppointments(patientApts.slice(0, pageEnd));
        setHasMoreAppointments(patientApts.length > pageEnd);
        setAppointmentsPage(page);
      } catch (err) {
        console.error("[CK-9213] Error loading appointments:", {
          errorCode: "CK-9213",
          errorMessage: err.message,
          timestamp: new Date().toISOString(),
        });
      } finally {
        setAppointmentsLoading(false);
      }
    },
    [clinicId, therapistId]
  );

  useEffect(() => {
    if (isOpen && selectedPatientId && !isPrefilledMode) {
      loadPatientAppointments(selectedPatientId);
    }
  }, [isOpen, selectedPatientId, isPrefilledMode, loadPatientAppointments]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSuccess(null);
      setError(null);
      setIsCreating(false);
      setSendEmail(false);
      setEmailAddress("");
      if (!isPrefilledMode) {
        setDocumentType(defaultDocType);
        setPaymentType(defaultPayType);
        setDescription("טיפול");
        setAmount("");
        setPatientName("");
        setSelectedPatientId(null);
        setSelectedAppointments([]);
        setPatientAppointments([]);
        setShowPatientDropdown(false);
        setPatientSearchQuery("");
      }
    }
  }, [isOpen, isPrefilledMode, defaultDocType, defaultPayType]);

  const handleSelectPatient = (patient) => {
    setSelectedPatientId(patient.id);
    setPatientName(`${patient.firstName} ${patient.lastName}`);
    setShowPatientDropdown(false);
    setPatientSearchQuery("");
    setSelectedAppointments([]);
  };

  const handleToggleAppointment = (apt) => {
    setSelectedAppointments((prev) => {
      const isSelected = prev.some((a) => a.id === apt.id);
      const newSelection = isSelected
        ? prev.filter((a) => a.id !== apt.id)
        : [...prev, apt];

      if (newSelection.length > 0) {
        const total = newSelection.reduce((sum, a) => {
          const rate = a.defaultHourlyRate || a.price || 0;
          return sum + rate;
        }, 0);
        if (total > 0) {
          setAmount(total.toString());
        }
      }

      return newSelection;
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  };

  const hasInvoice = (apt) => {
    return (
      apt.isProducedReceipitForThisMeeting === true ||
      (apt.RecepitsDocsRefArray && apt.RecepitsDocsRefArray.length > 0)
    );
  };

  const handleCreateInvoice = async () => {
    if (!patientName.trim()) {
      setError("יש להזין שם לקוח");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError("יש להזין סכום תקין");
      return;
    }
    if (sendEmail && emailAddress && !emailAddress.includes("@")) {
      setError("כתובת מייל לא תקינה");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const createDocument = httpsCallable(functions, "createDigitalInvoiceDocument");
      // Kanban/profile path: the transaction is not an "appointment" in the
      // picker, but it IS linked to one — pass that id so the Cloud Function
      // stamps RecepitsDocsRefArray / isProducedReceipitForThisMeeting on the
      // appointment and the catch-up carousel stops asking to invoice it again.
      const appointmentIds =
        selectedAppointments.length > 0
          ? selectedAppointments.map((apt) => apt.id)
          : prefilledTransaction?.appointmentId
            ? [prefilledTransaction.appointmentId]
            : [];

      const result = await createDocument({
        clinicId,
        documentData: {
          type: documentType,
          amount: Number(amount),
          description: description,
          paymentType: paymentType,
          client: {
            name: patientName,
            email: sendEmail ? emailAddress : undefined,
          },
          sendMailToClient: sendEmail && !!emailAddress,
          mailto: sendEmail ? emailAddress : "",
        },
        appointmentIds: appointmentIds.length > 0 ? appointmentIds : null,
        appointmentId: appointmentIds.length === 1 ? appointmentIds[0] : null,
        patientId: selectedPatientId || null,
      });

      if (result.data.success) {
        setSuccess(result.data);
        trackFeature(FEATURE_KEYS.DIGITAL_INVOICE);

        if (onSuccess) {
          onSuccess({
            documentId: result.data.document?.id,
            invoiceNumber: result.data.invoiceNumber,
            invoiceUrl: result.data.invoiceUrl,
            pdfUrl: result.data.pdfUrl,
            invoiceProvider: "digital_invoice",
            appointmentIds: appointmentIds,
            appointments: selectedAppointments,
            appointmentId: appointmentIds.length === 1 ? appointmentIds[0] : null,
            patientId: selectedPatientId,
          });
        }

        setTimeout(() => onClose(), 3000);
      } else {
        console.error("[CK-9212] Error creating invoice:", {
          errorCode: "CK-9212",
          error: result.data.error,
          timestamp: new Date().toISOString(),
        });
        setError(result.data.error || "שגיאה ביצירת החשבונית");
      }
    } catch (err) {
      console.error("[CK-9212] Error creating invoice:", {
        errorCode: "CK-9212",
        errorMessage: err.message,
        timestamp: new Date().toISOString(),
      });
      setError("שגיאה ביצירת החשבונית. נסה שוב.");
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="di-invoice-modal-overlay" onClick={onClose}>
      <div className="di-invoice-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="di-invoice-modal-header">
          <div className="di-invoice-header-icon">
            <Receipt size={24} weight="duotone" />
          </div>
          <div className="di-invoice-header-text">
            <h3>הפקת חשבונית</h3>
            <p>
              {isPrefilledMode
                ? "עבור תשלום שבוצע"
                : "בחר מטופל ופרטי החשבונית"}
            </p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Success State */}
        {success ? (
          <div className="di-invoice-modal-body di-success-state">
            <CheckCircle size={56} weight="fill" className="di-success-icon-large" />
            <h4>החשבונית נוצרה בהצלחה!</h4>
            <p>
              מספר מסמך: <strong>{success.invoiceNumber}</strong>
            </p>
            {selectedAppointments.length > 0 && (
              <p className="di-linked-info">
                <LinkSimple size={16} />
                <span>
                  החשבונית שויכה ל-{selectedAppointments.length}{" "}
                  {selectedAppointments.length === 1 ? "פגישה" : "פגישות"}
                </span>
              </p>
            )}
            {success.invoiceUrl && (
              <a
                href={success.invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="di-view-invoice-btn"
              >
                <Eye size={18} />
                <span>צפה בחשבונית</span>
              </a>
            )}
          </div>
        ) : (
          <>
            <div className="di-invoice-modal-body">
              {/* Patient Picker */}
              {!isPrefilledMode ? (
                <div className="di-form-group di-patient-picker">
                  <label>
                    <User size={16} />
                    בחר מטופל
                  </label>
                  <div className="di-patient-dropdown-container">
                    <button
                      type="button"
                      className="di-patient-dropdown-trigger"
                      onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                      disabled={isCreating}
                    >
                      <span>
                        {selectedPatientId
                          ? patientName
                          : "בחר מטופל מהרשימה..."}
                      </span>
                      <CaretDown size={16} />
                    </button>

                    {showPatientDropdown && (
                      <div className="di-patient-dropdown">
                        <input
                          type="text"
                          className="di-patient-search"
                          placeholder="חפש מטופל..."
                          value={patientSearchQuery}
                          onChange={(e) => setPatientSearchQuery(e.target.value)}
                          autoFocus
                        />
                        <div className="di-patient-list">
                          {filteredPatients.length === 0 ? (
                            <div className="di-patient-empty">לא נמצאו מטופלים</div>
                          ) : (
                            filteredPatients.map((patient) => (
                              <button
                                key={patient.id}
                                className={`di-patient-option ${
                                  selectedPatientId === patient.id ? "selected" : ""
                                }`}
                                onClick={() => handleSelectPatient(patient)}
                              >
                                <span>
                                  {patient.firstName} {patient.lastName}
                                </span>
                                {selectedPatientId === patient.id && (
                                  <Check size={16} weight="bold" />
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="di-prefilled-patient">
                  <User size={16} />
                  <span>{patientName}</span>
                </div>
              )}

              {/* Patient Appointments */}
              {selectedPatientId && !isPrefilledMode && (
                <div className="di-appointments-section">
                  <label>
                    <CalendarBlank size={16} />
                    פגישות (בחר לשיוך)
                    {selectedAppointments.length > 0 && (
                      <span className="di-selected-count">
                        {selectedAppointments.length} נבחרו
                        <button
                          type="button"
                          className="di-clear-selection"
                          onClick={() => setSelectedAppointments([])}
                        >
                          נקה
                        </button>
                      </span>
                    )}
                  </label>

                  {appointmentsLoading ? (
                    <ContentLoader size="sm" compact message="טוען פגישות..." />
                  ) : patientAppointments.length === 0 ? (
                    <div className="di-no-appointments">אין פגישות שהושלמו</div>
                  ) : (
                    <>
                      <div className="di-appointments-list">
                        {patientAppointments.map((apt) => {
                          const isSelected = selectedAppointments.some(
                            (a) => a.id === apt.id
                          );
                          const alreadyInvoiced = hasInvoice(apt);
                          return (
                            <button
                              key={apt.id}
                              type="button"
                              className={`di-appointment-item ${isSelected ? "selected" : ""} ${
                                alreadyInvoiced ? "invoiced" : ""
                              }`}
                              onClick={() => !alreadyInvoiced && handleToggleAppointment(apt)}
                              disabled={alreadyInvoiced}
                            >
                              <div className="di-apt-checkbox">
                                {isSelected ? (
                                  <Check size={14} weight="bold" />
                                ) : alreadyInvoiced ? (
                                  <FileText size={14} />
                                ) : null}
                              </div>
                              <div className="di-apt-info">
                                <span className="di-apt-date">
                                  {formatDate(apt.startTime)}
                                </span>
                                <span className="di-apt-desc">
                                  {apt.treatmentType || "טיפול"}
                                </span>
                              </div>
                              <span className="di-apt-amount">
                                {formatCurrency(
                                  apt.defaultHourlyRate || apt.price || 0
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {hasMoreAppointments && (
                        <button
                          type="button"
                          className="di-load-more"
                          onClick={() =>
                            loadPatientAppointments(
                              selectedPatientId,
                              appointmentsPage + 1
                            )
                          }
                        >
                          טען עוד
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Form Fields */}
              <div className="di-invoice-form">
                {!isPrefilledMode && (
                  <div className="di-form-group">
                    <label>
                      <User size={16} />
                      שם לקוח
                    </label>
                    <input
                      type="text"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      placeholder="שם הלקוח בחשבונית"
                      disabled={isCreating}
                    />
                  </div>
                )}

                <div className="di-form-row">
                  <div className="di-form-group">
                    <label>
                      <CurrencyCircleDollar size={16} />
                      סכום
                    </label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      dir="ltr"
                      disabled={isCreating}
                    />
                  </div>
                  <div className="di-form-group">
                    <label>
                      <FileText size={16} />
                      סוג מסמך
                    </label>
                    <select
                      value={documentType}
                      onChange={(e) => setDocumentType(Number(e.target.value))}
                      disabled={isCreating}
                    >
                      {getDocumentTypeOptions().map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="di-form-row">
                  <div className="di-form-group">
                    <label>תיאור</label>
                    <input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="טיפול"
                      disabled={isCreating}
                    />
                  </div>
                  <div className="di-form-group">
                    <label>אמצעי תשלום</label>
                    <select
                      value={paymentType}
                      onChange={(e) => setPaymentType(Number(e.target.value))}
                      disabled={isCreating}
                    >
                      {getPaymentTypeOptions().map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Email Toggle */}
                <div className="di-email-section">
                  <div className="di-email-toggle-row">
                    <label className="di-email-toggle-label">
                      <EnvelopeSimple size={16} />
                      שלח חשבונית במייל
                    </label>
                    <label className="di-toggle-switch-sm">
                      <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        disabled={isCreating}
                      />
                      <span className="di-toggle-slider-sm" />
                    </label>
                  </div>
                  {sendEmail && (
                    <input
                      type="email"
                      className="di-email-input"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="email@example.com"
                      dir="ltr"
                      disabled={isCreating}
                    />
                  )}
                </div>
              </div>

              {error && (
                <div className="di-invoice-error">
                  <WarningCircle size={16} weight="fill" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="di-invoice-modal-footer">
              <button
                className="di-invoice-cancel-btn"
                onClick={onClose}
                disabled={isCreating}
              >
                ביטול
              </button>
              <button
                className="di-invoice-create-btn"
                onClick={handleCreateInvoice}
                disabled={isCreating || !patientName.trim() || !amount}
              >
                {isCreating ? (
                  <>
                    <LoaderLoop size="xs" />
                    <span>יוצר חשבונית...</span>
                  </>
                ) : (
                  <>
                    <Receipt size={16} weight="bold" />
                    <span>הפק חשבונית</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DigitalInvoiceQuickModal;
